/**
 * Festival-timezone-aware conversion helpers.
 *
 * `datetime-local` HTML inputs return strings shaped like "YYYY-MM-DDTHH:mm"
 * with no timezone. Festival times are entered as wallclock-local in the
 * festival's timezone, but stored on the server as UTC. These helpers convert
 * between the two representations using `Intl.DateTimeFormat`.
 *
 * No external dependency — all logic uses the platform Intl API.
 */

function getOffsetMinutesAt(utcMs: number, tz: string): number {
  // Returns the offset of `tz` relative to UTC at the moment `utcMs`.
  // Positive for east of UTC. Computed by formatting the same instant in `tz`
  // and reading off the wallclock components.
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = dtf.formatToParts(new Date(utcMs));
  const map: Record<string, number> = {
    year: 0, month: 1, day: 1, hour: 0, minute: 0, second: 0,
  };
  for (const p of parts) {
    if (p.type !== "literal") map[p.type] = parseInt(p.value, 10);
  }
  const asIfUTC = Date.UTC(
    map.year as number,
    (map.month as number) - 1,
    map.day as number,
    map.hour as number,
    map.minute as number,
    map.second as number
  );
  return Math.round((asIfUTC - utcMs) / 60000);
}

/**
 * Convert a `datetime-local` input value (no TZ) to a UTC ISO string,
 * interpreting the wallclock components as time in `tz`.
 *
 * Example: `localInputToUTC("2026-08-01T22:00", "Asia/Tokyo")` →
 *          `"2026-08-01T13:00:00.000Z"`.
 */
export function localInputToUTC(value: string, tz: string): string {
  if (!value) return value;
  const [datePart = "1970-01-01", timePart = "00:00"] = value.split("T");
  const [yStr = "1970", mStr = "1", dStr = "1"] = datePart.split("-");
  const [hhStr = "0", mmStr = "0"] = timePart.split(":");
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  const hh = parseInt(hhStr, 10);
  const mm = parseInt(mmStr, 10);

  // First approximation: pretend the components are UTC.
  const naiveUTC = Date.UTC(y, m - 1, d, hh, mm);
  // Compute the offset of `tz` at that instant, and shift.
  const offsetMin = getOffsetMinutesAt(naiveUTC, tz);
  return new Date(naiveUTC - offsetMin * 60 * 1000).toISOString();
}

/**
 * Convert a UTC ISO string to the `datetime-local` form (no TZ) shown to
 * a user thinking in `tz` wallclock.
 *
 * Example: `utcToLocalInput("2026-08-01T13:00:00.000Z", "Asia/Tokyo")` →
 *          `"2026-08-01T22:00"`.
 */
export function utcToLocalInput(iso: string, tz: string): string {
  if (!iso) return iso;
  const dt = new Date(iso);
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = dtf.formatToParts(dt);
  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}`;
}
